import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, Edit3, Eye, EyeOff, Save, X, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import ExportImportBar from './ExportImportBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONTS = {
  playfair: { fontFamily: '"Playfair Display", Georgia, serif' },
  outfit: { fontFamily: '"Outfit", system-ui, sans-serif' },
};

const COLOR_PRESETS = [
  { value: 'blue', label: 'Bleu' },
  { value: 'amber', label: 'Ambre' },
  { value: 'emerald', label: 'Émeraude' },
  { value: 'slate', label: 'Ardoise' },
  { value: 'green', label: 'Vert' },
  { value: 'purple', label: 'Pourpre' },
  { value: 'yellow', label: 'Or' },
  { value: 'red', label: 'Rouge' },
];

const emptyForm = {
  label: '',
  icon: '',
  color: 'blue',
  description: '',
  is_visible_public: true,
};

const TypesMaitresManager = ({ onTypesChange }) => {
  const { getAuthHeader } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const headers = () => ({ headers: getAuthHeader() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tarifs-types-maitres`, headers());
      setTypes(res.data?.types || []);
      onTypesChange?.(res.data?.types || []);
    } catch {
      toast.error('Impossible de charger les types maîtres');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allSelected = types.length > 0 && selectedIds.size === types.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < types.length;

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(types.map((t) => t.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const openCreate = () => {
    setEditingType(null);
    setForm(emptyForm);
    setShowCreateModal(true);
  };
  const openEdit = (t) => {
    setEditingType(t);
    setForm({
      label: t.label,
      icon: t.icon || '',
      color: t.color || 'blue',
      description: t.description || '',
      is_visible_public: !!t.is_visible_public,
    });
    setShowCreateModal(true);
  };

  const submit = async () => {
    if (!form.label.trim()) {
      toast.error('Libellé requis');
      return;
    }
    try {
      if (editingType) {
        await axios.put(
          `${API}/tarifs-types-maitres/${editingType.id}`,
          form,
          headers(),
        );
        toast.success('Type mis à jour');
      } else {
        await axios.post(`${API}/tarifs-types-maitres`, form, headers());
        toast.success('Type créé');
      }
      setShowCreateModal(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur';
      toast.error(typeof msg === 'string' ? msg : 'Erreur');
    }
  };

  const duplicate = async (id) => {
    try {
      await axios.post(`${API}/tarifs-types-maitres/${id}/duplicate`, {}, headers());
      toast.success('Type dupliqué (masqué par défaut)');
      load();
    } catch {
      toast.error('Échec de la duplication');
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm('Supprimer ce type ?')) return;
    try {
      await axios.delete(`${API}/tarifs-types-maitres/${id}`, headers());
      toast.success('Type supprimé');
      load();
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  const bulkDelete = async () => {
    try {
      const res = await axios.post(
        `${API}/tarifs-types-maitres/bulk-delete`,
        { ids: Array.from(selectedIds) },
        headers(),
      );
      toast.success(`${res.data?.deleted ?? selectedIds.size} type(s) supprimé(s)`);
      setSelectedIds(new Set());
      setConfirmBulkDelete(false);
      load();
    } catch {
      toast.error('Échec de la suppression en masse');
    }
  };

  const bulkSetVisibility = async (makeVisible) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await axios.post(
        `${API}/tarifs-types-maitres/bulk-visibility`,
        { ids: Array.from(selectedIds), is_visible_public: makeVisible },
        headers(),
      );
      const n = res.data?.updated ?? selectedIds.size;
      toast.success(
        makeVisible
          ? `${n} type(s) rendu(s) public(s)`
          : `${n} type(s) masqué(s)`,
      );
      setSelectedIds(new Set());
      load();
    } catch {
      toast.error('Échec de la modification en masse');
    }
  };

  const toggleVisibility = async (t) => {
    try {
      await axios.put(
        `${API}/tarifs-types-maitres/${t.id}`,
        { is_visible_public: !t.is_visible_public },
        headers(),
      );
      load();
    } catch {
      toast.error('Impossible de modifier la visibilité');
    }
  };

  // --- Drag-and-drop ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = types.findIndex((t) => t.id === active.id);
    const newIndex = types.findIndex((t) => t.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(types, oldIndex, newIndex);
    setTypes(next);  // optimistic UI
    try {
      await axios.post(
        `${API}/tarifs-types-maitres/reorder`,
        { ordered_ids: next.map((t) => t.id) },
        headers(),
      );
      toast.success('Ordre enregistré');
      onTypesChange?.(next);
    } catch {
      toast.error('Échec de la réorganisation');
      load();  // rollback à l'état serveur
    }
  };

  return (
    <>
      <Card className="border-slate-200 shadow-md rounded-2xl overflow-hidden" data-testid="types-maitres-manager">
        <div
          className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200"
          style={{ background: 'linear-gradient(135deg,#0a1e3f 0%,#0B3C5D 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center">
              <Edit3 className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-amber-400 font-semibold" style={FONTS.outfit}>
                Catégories maîtres
              </p>
              <h3 className="text-white font-bold text-lg" style={FONTS.playfair}>
                Gérer les types
              </h3>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              disabled={selectedIds.size === 0}
              onClick={() => bulkSetVisibility(true)}
              data-testid="bulk-show-btn"
              title="Rendre publics les types sélectionnés"
            >
              <Eye className="w-4 h-4 mr-1.5" />
              Rendre publics
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              disabled={selectedIds.size === 0}
              onClick={() => bulkSetVisibility(false)}
              data-testid="bulk-hide-btn"
              title="Masquer du formulaire public les types sélectionnés"
            >
              <EyeOff className="w-4 h-4 mr-1.5" />
              Masquer
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              disabled={selectedIds.size === 0}
              onClick={() => setConfirmBulkDelete(true)}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Supprimer ({selectedIds.size})
            </Button>
            <Button
              size="sm"
              className="bg-amber-400 hover:bg-amber-300 text-[#0a1e3f] font-semibold"
              onClick={openCreate}
              data-testid="create-type-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Nouveau type
            </Button>
          </div>
        </div>

        {/* Barre Export/Import */}
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
          <ExportImportBar
            collection="tarifs_types_maitres"
            label="Types Maîtres"
            onImported={load}
          />
        </div>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={types.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <table className="w-full text-sm" data-testid="types-maitres-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-slate-600 text-xs uppercase tracking-wider">
                      <th className="px-2 py-3 w-8" aria-label="Réordonner" />
                      <th className="px-3 py-3 w-10">
                        <Checkbox
                          checked={allSelected}
                          data-state={someSelected ? 'indeterminate' : (allSelected ? 'checked' : 'unchecked')}
                          onCheckedChange={toggleAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="px-3 py-3">Type</th>
                      <th className="px-3 py-3 hidden md:table-cell">Slug</th>
                      <th className="px-3 py-3 hidden lg:table-cell">Description</th>
                      <th className="px-3 py-3 text-center">Visible public</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid="types-maitres-tbody">
                    {loading && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Chargement…</td></tr>
                    )}
                    {!loading && types.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Aucun type — cliquez sur « Nouveau type »</td></tr>
                    )}
                    {types.map((t) => (
                      <SortableTypeRow
                        key={t.id}
                        type={t}
                        isSelected={selectedIds.has(t.id)}
                        onToggleSelect={() => toggleOne(t.id)}
                        onToggleVisibility={() => toggleVisibility(t)}
                        onEdit={() => openEdit(t)}
                        onDuplicate={() => duplicate(t.id)}
                        onDelete={() => removeOne(t.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        </CardContent>
      </Card>

      {/* Modal Création / Édition */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg" data-testid="type-modal">
          <DialogHeader>
            <DialogTitle style={FONTS.playfair}>
              {editingType ? 'Modifier le type' : 'Nouveau type maître'}
            </DialogTitle>
            <DialogDescription>
              {editingType
                ? 'Mettre à jour ce type maître et sa visibilité publique.'
                : 'Créer une nouvelle catégorie de demande, visible côté formulaire public si activée.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Libellé *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ex. Maritime, Tourisme, Cybersécurité…"
                data-testid="type-label-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Icône (emoji)</Label>
                <Input
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  placeholder="🚢"
                  maxLength={4}
                  data-testid="type-icon-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Couleur</Label>
                <select
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                  data-testid="type-color-select"
                >
                  {COLOR_PRESETS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description optionnelle affichée à l'admin"
                data-testid="type-description-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={form.is_visible_public}
                onCheckedChange={(v) => setForm({ ...form, is_visible_public: !!v })}
                id="visibility-checkbox"
                data-testid="type-visibility-checkbox"
              />
              <Label htmlFor="visibility-checkbox" className="text-sm text-slate-700 cursor-pointer">
                Visible sur le formulaire public de demande
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              <X className="w-4 h-4 mr-1.5" /> Annuler
            </Button>
            <Button onClick={submit} className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white" data-testid="type-submit-btn">
              <Save className="w-4 h-4 mr-1.5" />
              {editingType ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal confirmation bulk delete */}
      <Dialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle style={FONTS.playfair} className="text-red-700">
              Supprimer {selectedIds.size} type(s) ?
            </DialogTitle>
            <DialogDescription>
              Action irréversible. Confirmez la suppression définitive des types sélectionnés.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Cette action est <strong>irréversible</strong>. Les tarifs liés à ces types resteront en base
            mais ne seront plus catégorisés. Confirmez-vous ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulkDelete(false)}>
              Annuler
            </Button>
            <Button onClick={bulkDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TypesMaitresManager;

// ---------------------------------------------------------------------------
// Composant interne : ligne draggable
// ---------------------------------------------------------------------------
function SortableTypeRow({
  type, isSelected, onToggleSelect, onToggleVisibility,
  onEdit, onDuplicate, onDelete,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: type.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? '#f1f5f9' : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
      data-testid={`type-row-${type.value}`}
    >
      <td className="px-2 py-3 w-8 align-middle">
        <button
          type="button"
          aria-label="Déplacer"
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-grab active:cursor-grabbing touch-none"
          data-testid={`drag-handle-${type.value}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </td>
      <td className="px-3 py-3 align-middle">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelect}
          data-testid={`type-checkbox-${type.value}`}
        />
      </td>
      <td className="px-3 py-3 align-middle">
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none">{type.icon || '🏷️'}</span>
          <span className="font-medium text-slate-900">{type.label}</span>
        </div>
      </td>
      <td className="px-3 py-3 hidden md:table-cell text-slate-500 font-mono text-xs align-middle">{type.value}</td>
      <td className="px-3 py-3 hidden lg:table-cell text-slate-500 truncate max-w-xs align-middle">{type.description || '—'}</td>
      <td className="px-3 py-3 text-center align-middle">
        <button
          onClick={onToggleVisibility}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition ${
            type.is_visible_public
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
          }`}
          data-testid={`toggle-visibility-${type.value}`}
        >
          {type.is_visible_public
            ? <><Eye className="w-3 h-3" /> Public</>
            : <><EyeOff className="w-3 h-3" /> Masqué</>}
        </button>
      </td>
      <td className="px-3 py-3 text-right align-middle">
        <div className="inline-flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`edit-type-${type.value}`}>
            <Edit3 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDuplicate} data-testid={`duplicate-type-${type.value}`}>
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 hover:text-red-700"
            onClick={onDelete}
            data-testid={`delete-type-${type.value}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

